<script lang="ts">
  // HR > Remote Punches (#/remote) — a read-only view of every field-work
  // punch (GET /api/work_logs/remote, RequireHR). Deliberately has no
  // approve/reject/edit affordance: a remote punch is a declaration by the
  // employee, not a request, and nothing in the API can act on one.
  import { createQuery } from '@tanstack/svelte-query';
  import { MapPin, ExternalLink } from 'lucide-svelte';
  import { workLogsApi } from '$lib/api/workLogs';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import StatusBadge from '$lib/components/ui/StatusBadge.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import type { RemoteWorkLog } from '$lib/types';

  const WINDOWS = [7, 30, 90] as const;
  let days = $state<number>(30);

  const query = createQuery(() => ({
    queryKey: ['remote-work-logs', days],
    queryFn: () => workLogsApi.remoteList(days)
  }));

  const rows = $derived(query.data?.work_logs ?? []);
  // Each window is a separate request, so only the selected chip can carry a
  // count — showing a stale or guessed number on the others would be worse
  // than showing none.
  const openCount = $derived(rows.filter((r) => !r.punch_out).length);

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  function fmtTime(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
</script>

<div class="tym-page">
  <Toolbar {filters} {actions} />

  {#if query.isError}
    <div class="tym-error-state">Could not load remote punches.</div>
  {:else}
    <DataTable
      dense
      mobileCard={punchCard}
      loading={query.isLoading}
      skeletonRows={8}
      columns={[
        { key: 'employee', label: 'Employee', cell: employeeCell },
        { key: 'date', label: 'Date', cell: dateCell },
        { key: 'in', label: 'In', cell: inCell },
        { key: 'out', label: 'Out', cell: outCell },
        { key: 'hours', label: 'Hours', align: 'right', cell: hoursCell },
        { key: 'location', label: 'Location', cell: locationCell },
        { key: 'link', label: 'Map', cell: linkCell }
      ]}
      {rows}
      rowKey={(r) => r.id}
      emptyTitle="No remote punches"
      emptyBody="Nobody has filed a field-work punch in this window."
    />
  {/if}
</div>

{#snippet filters()}
  <div class="flex flex-wrap items-center gap-1.5">
    {#each WINDOWS as w (w)}
      <button
        type="button"
        class="tym-chip {days === w ? 'active' : ''}"
        aria-pressed={days === w}
        onclick={() => (days = w)}
      >
        Last {w}d
        {#if days === w && !query.isLoading}
          <span class="tym-chip-count">{rows.length}</span>
        {/if}
      </button>
    {/each}
  </div>
{/snippet}
{#snippet actions()}
  {#if openCount > 0}
    <span class="text-[12px] font-medium text-toyama-teal">
      {openCount} still punched in
    </span>
  {/if}
{/snippet}

{#snippet punchCard(r: RemoteWorkLog)}
  <!-- Hand-built rather than derived from the columns: the whole point of a
       remote punch is *where*, and the location cell is a two-line block that
       won't sit inline in the generic meta row. -->
  <div class="min-w-0 flex-1">
    <div class="flex items-baseline justify-between gap-2">
      <span class="truncate font-medium text-navy">{r.user_name || r.user_email}</span>
      <span class="tym-num shrink-0 text-[12px] text-dark-gray">{fmtDate(r.punch_in)}</span>
    </div>
    <div class="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-dark-gray">
      <span class="tym-num">{fmtTime(r.punch_in)} → {r.punch_out ? fmtTime(r.punch_out) : ''}</span>
      {#if r.punch_out}
        <span class="tym-num">{r.hours}h</span>
      {:else}
        <StatusBadge kind="info" label="Still in" />
      {/if}
    </div>
    <div class="mt-1.5 flex items-start gap-1.5 text-[12px]">
      <MapPin class="mt-0.5 h-3.5 w-3.5 shrink-0 text-toyama-teal" />
      <span class="min-w-0">
        <span class="block font-medium text-navy">{r.location_name || '—'}</span>
        {#if r.location_area && r.location_area !== '—'}
          <span class="block text-[11.5px] text-dark-gray">{r.location_area}</span>
        {/if}
      </span>
      {#if r.maps_link}
        <a
          class="ml-auto inline-flex shrink-0 items-center gap-1 text-toyama-teal underline"
          href={r.maps_link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Map <ExternalLink class="h-3 w-3" />
        </a>
      {/if}
    </div>
  </div>
{/snippet}

{#snippet employeeCell(r: RemoteWorkLog)}
  <span class="font-medium text-navy">{r.user_name || r.user_email}</span>
{/snippet}
{#snippet dateCell(r: RemoteWorkLog)}
  <span class="tym-num muted whitespace-nowrap">{fmtDate(r.punch_in)}</span>
{/snippet}
{#snippet inCell(r: RemoteWorkLog)}
  <span class="tym-num whitespace-nowrap">{fmtTime(r.punch_in)}</span>
{/snippet}
{#snippet outCell(r: RemoteWorkLog)}
  <!-- An open punch is a status, not a missing time — badge it so it stops
       reading as one more timestamp in the column. -->
  {#if r.punch_out}
    <span class="tym-num muted whitespace-nowrap">{fmtTime(r.punch_out)}</span>
  {:else}
    <StatusBadge kind="info" label="Still in" />
  {/if}
{/snippet}
{#snippet hoursCell(r: RemoteWorkLog)}
  <span class="tym-num muted">{r.punch_out ? `${r.hours}h` : '—'}</span>
{/snippet}
{#snippet locationCell(r: RemoteWorkLog)}
  <span class="flex items-start gap-1.5">
    <MapPin class="mt-0.5 h-3.5 w-3.5 shrink-0 text-toyama-teal" />
    <span class="min-w-0">
      <span class="block font-medium text-navy">{r.location_name || '—'}</span>
      {#if r.location_area && r.location_area !== '—'}
        <span class="block text-[11.5px] text-dark-gray">{r.location_area}</span>
      {/if}
    </span>
  </span>
{/snippet}
{#snippet linkCell(r: RemoteWorkLog)}
  {#if r.maps_link}
    <a
      class="inline-flex items-center gap-1 text-toyama-teal underline"
      href={r.maps_link}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open <ExternalLink class="h-3 w-3" />
    </a>
  {:else}
    <span class="muted">—</span>
  {/if}
{/snippet}
